import React, { useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress, Alert, Snackbar, Chip } from '@mui/material';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../hooks/useAuth';
import { toUserTimezone, toUTC } from '../../utils/dateUtils';

const fetchWorkOrders = async (start, end) => {
  const { data } = await axios.get('/workorders/calendar', {
    params: { start: start.toISOString(), end: end.toISOString() }
  });
  return data;
};

const updateSchedule = async ({ id, start, end }) => {
  const { data } = await axios.patch(`/workorders/${id}/schedule`, { start, end });
  return data;
};

const WorkOrderCalendar = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dateRange, setDateRange] = useState({ start: new Date(), end: new Date() });

  const userTimezone = user?.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeFormat = user?.settings?.timeFormat || '24h';

  const { data: events, isLoading, error } = useQuery({
    queryKey: ['workorders', 'calendar', dateRange.start, dateRange.end],
    queryFn: () => fetchWorkOrders(dateRange.start, dateRange.end),
  });

  const scheduleMutation = useMutation({
    mutationFn: updateSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries(['workorders']);
      setSnackbar({ open: true, message: 'Schedule updated', severity: 'success' });
    },
    onError: () => setSnackbar({ open: true, message: 'Update failed', severity: 'error' }),
  });

  const handleEventDrop = (info) => {
    const startUTC = toUTC(info.event.start, userTimezone);
    const endUTC = toUTC(info.event.end, userTimezone);
    scheduleMutation.mutate({ id: info.event.id, start: startUTC, end: endUTC });
  };

  const handleEventResize = (info) => {
    const startUTC = toUTC(info.event.start, userTimezone);
    const endUTC = toUTC(info.event.end, userTimezone);
    scheduleMutation.mutate({ id: info.event.id, start: startUTC, end: endUTC });
  };

  const handleDatesSet = (dateInfo) => {
    setDateRange({ start: dateInfo.start, end: dateInfo.end });
  };

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">Error loading calendar: {error.message}</Alert>;

  const displayEvents = events?.map(event => ({
    ...event,
    start: toUserTimezone(event.start, userTimezone),
    end: toUserTimezone(event.end, userTimezone),
  }));

  return (
    <Box>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
        initialView="timeGridWeek"
        editable
        events={displayEvents}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        datesSet={handleDatesSet}
        height="auto"
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        slotLabelFormat={{ hour: timeFormat === '24h' ? '2-digit' : 'numeric', minute: '2-digit', hour12: timeFormat === '12h' }}
        eventContent={(arg) => {
          const statusColor = { pending: '#ff9800', 'in-progress': '#2196f3', completed: '#4caf50', cancelled: '#f44336' };
          return (
            <div style={{ backgroundColor: statusColor[arg.event.extendedProps.status] || '#9e9e9e', color: 'white', padding: '2px 4px', borderRadius: '4px', fontSize: '12px' }}>
              <b>{arg.event.title}</b><br />
              <small>{arg.event.extendedProps.mechanic}</small>
            </div>
          );
        }}
      />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkOrderCalendar;